import { useMemo, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useCatalogColumns } from "../lib/catalogColumns";
import { exportIngredientsToCsv } from "../lib/ingredientsCsv";
import { PageHeader } from "../components/PageHeader";
import { EmptyState } from "../components/EmptyState";
import { IngredientForm } from "../components/IngredientForm";
import { IngredientMergeTool } from "../components/IngredientMergeTool";
import { CatalogColumnPicker } from "../components/CatalogColumnPicker";
import type { Ingredient, Store, StoreSection } from "../types";

interface StoresResp {
  stores: Store[];
}
interface StoreDetail {
  store: Store;
  sections: StoreSection[];
}

const INGREDIENT_COLUMNS = [
  { id: "name" as const, label: "Název", defaultVisible: true, locked: true },
  { id: "unit" as const, label: "Jednotka", defaultVisible: true },
  { id: "packageSize" as const, label: "Velikost balení", defaultVisible: true },
  { id: "storeSections" as const, label: "Sekce v obchodech", defaultVisible: true },
  { id: "actions" as const, label: "Akce", defaultVisible: true, locked: true },
];

type IngredientColumnId = (typeof INGREDIENT_COLUMNS)[number]["id"];

export default function IngredientsPage() {
  const qc = useQueryClient();
  const ingredientsQ = useQuery({
    queryKey: ["ingredients"],
    queryFn: () => api.get<{ ingredients: Ingredient[] }>("/api/ingredients"),
  });
  const storesQ = useQuery({
    queryKey: ["stores"],
    queryFn: () => api.get<StoresResp>("/api/stores"),
  });
  const storeIds = storesQ.data?.stores.map((s) => s.id) ?? [];
  const storeDetailsQs = useQueries({
    queries: storeIds.map((id) => ({
      queryKey: ["store", id],
      queryFn: () => api.get<StoreDetail>(`/api/stores/${id}`),
    })),
  });
  const sectionsByStore = useMemo(() => {
    const map = new Map<string, StoreSection[]>();
    for (const q of storeDetailsQs) {
      if (q.data) map.set(q.data.store.id, q.data.sections);
    }
    return map;
  }, [storeDetailsQs.map((q) => q.dataUpdatedAt).join("|")]);

  const { visible, visibleColumns, toggle, isVisible } = useCatalogColumns(
    "jedalnicek:columns:ingredients",
    INGREDIENT_COLUMNS
  );

  const [editing, setEditing] = useState<Ingredient | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [filter, setFilter] = useState("");

  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/api/ingredients/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ingredients"] }),
  });

  const allIngredients = ingredientsQ.data?.ingredients ?? [];

  const list = useMemo(() => {
    if (!filter.trim()) return allIngredients;
    const f = filter.trim().toLowerCase();
    return allIngredients.filter((i) => i.name.toLowerCase().includes(f));
  }, [allIngredients, filter]);

  const handleExportCsv = () => {
    exportIngredientsToCsv(allIngredients, storesQ.data?.stores ?? [], sectionsByStore);
  };

  return (
    <div>
      <PageHeader
        icon="🥕"
        title="Suroviny"
        description="Katalog surovin s jednotkami, velikostí balení a sekcemi v obchodech."
        actions={
          <>
            <button
              type="button"
              className="btn-secondary flex items-center gap-1"
              onClick={handleExportCsv}
              disabled={allIngredients.length === 0}
              title="Exportovat celý katalog do CSV"
            >
              <span aria-hidden>📤</span>
              <span>Export CSV</span>
            </button>
            <button
              type="button"
              className="btn-secondary flex items-center gap-1"
              onClick={() => setMergeOpen(true)}
              disabled={allIngredients.length < 2}
              title={allIngredients.length < 2 ? "Ke sloučení potřebujete alespoň 2 suroviny" : undefined}
            >
              <span aria-hidden>🔗</span>
              <span>Sloučit</span>
            </button>
            <button className="btn-primary flex items-center gap-1" onClick={() => setNewOpen(true)}>
              <span aria-hidden>➕</span>
              <span>Přidat surovinu</span>
            </button>
          </>
        }
      />

      <div className="flex flex-col sm:flex-row gap-2 mb-4 sm:items-center">
        <input
          className="input max-w-md"
          placeholder="🔍 Hledat surovinu…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <div className="sm:ml-auto">
          <CatalogColumnPicker columns={INGREDIENT_COLUMNS} visible={visible} onToggle={toggle} />
        </div>
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon={filter ? "🔍" : "🥕"}
          title={filter ? "Nic nenalezeno" : "Zatím nemáte žádné suroviny"}
          description={filter ? undefined : "Přidejte první surovinu a přiřaďte jí sekci v obchodě."}
        />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                {visibleColumns.map((col) => (
                  <th
                    key={col.id}
                    className={`px-4 py-2 ${col.id === "actions" ? "text-right" : ""}`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {list.map((i) => (
                <tr key={i.id}>
                  {isVisible("name") && <td className="px-4 py-2 font-medium">{i.name}</td>}
                  {isVisible("unit") && <td className="px-4 py-2">{i.unit}</td>}
                  {isVisible("packageSize") && (
                    <td className="px-4 py-2">
                      {i.packageSize === 0 ? (
                        <span
                          className="text-[10px] uppercase tracking-wide rounded-full bg-amber-50 text-amber-700 px-2 py-0.5"
                          title="Surovina se prodává volně, bez fixního balení"
                        >
                          Na váhu
                        </span>
                      ) : (
                        <>
                          {i.packageSize} {i.unit}
                        </>
                      )}
                    </td>
                  )}
                  {isVisible("storeSections") && (
                    <td className="px-4 py-2 text-xs">
                      {(i.storeSections ?? []).length === 0 ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        (i.storeSections ?? []).map((m) => {
                          const store = storesQ.data?.stores.find((s) => s.id === m.storeId);
                          const section = sectionsByStore.get(m.storeId)?.find((s) => s.id === m.sectionId);
                          return (
                            <span
                              key={`${m.storeId}-${m.sectionId}`}
                              className="inline-block mr-2 mb-1 rounded bg-slate-100 px-2 py-0.5"
                            >
                              {store?.name ?? "?"} · {section?.name ?? "?"}
                            </span>
                          );
                        })
                      )}
                    </td>
                  )}
                  {isVisible("actions") && (
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button
                        className="btn-ghost text-xs inline-flex items-center gap-1"
                        onClick={() => setEditing(i)}
                        title="Upravit surovinu"
                      >
                        <span aria-hidden>✏️</span>
                        <span>Upravit</span>
                      </button>
                      <button
                        className="btn-ghost text-xs text-red-600 inline-flex items-center gap-1"
                        title="Smazat surovinu"
                        onClick={() => {
                          if (confirm(`Smazat surovinu "${i.name}"?`)) del.mutate(i.id);
                        }}
                      >
                        <span aria-hidden>🗑️</span>
                        <span>Smazat</span>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(newOpen || editing) && (
        <IngredientForm
          ingredient={editing}
          stores={storesQ.data?.stores ?? []}
          sectionsByStore={sectionsByStore}
          onClose={() => {
            setNewOpen(false);
            setEditing(null);
          }}
        />
      )}

      <IngredientMergeTool
        open={mergeOpen}
        ingredients={allIngredients}
        onClose={() => setMergeOpen(false)}
        onSuccess={() => {
          qc.invalidateQueries({ queryKey: ["ingredients"] });
          qc.invalidateQueries({ queryKey: ["recipes"] });
          qc.invalidateQueries({ queryKey: ["fridge"] });
        }}
      />
    </div>
  );
}
